/** @jsxImportSource @emotion/react */
import React, {
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { ControlBtnGroup, SubTitleGroup, SubTitleLayout } from 'components/layouts/ContentLayout';
import { CellType } from '@grapecity/wijmo.grid';
import { CellMaker } from '@grapecity/wijmo.grid.cellmaker';
import CustomGrid from 'components/grids/CustomGrid';
import { Button } from '@mui/material';
import { IconButton } from 'components/buttons/IconSVG';
import { useLoading } from 'components/process/Loading';
import { useMessageBar } from 'hooks/useMessageBar';
import { IssueSetup } from 'models/setup/issue/issue';
import { SetupIssue, SetupIssueCondition } from 'models/setup/SetupDailyReport';
import useEvent from 'react-use-event-hook';
import { getIssueSetupList } from 'apis/setup/issue/IssueSetup';
import { PageMode } from 'models/common/Common';
import { ButtonDesign } from 'components/buttons/ButtonStyle';
import ProjectIssueCreatePage from '../issue/ProjectIssueRegisterPage';

/**
 * @desc 설비셋업관리 > 셋업일보조회
 * @author
 * -------------------------------------------------------------------------------------
 *  Date.       author         Description
 * -------------------------------------------------------------------------------------
 * 2025.11.11   jd             신규 생성
 */
interface Props {
  readOnly: boolean;
  onChangeFolding: (folding: boolean) => void;
}

const SetupDailyReportIssueContent = (props: Props, ref) => {
  const { t } = useTranslation();
  const { openLoading } = useLoading();
  const { openMessageBar } = useMessageBar();

  const issueGridRef = useRef<any>();
  const [issueCondition, setIssueCondition] = useState<SetupIssueCondition>({});
  const [issueRowData, setIssueRowData] = useState<SetupIssue[]>([]);
  const [isOpenFolding, setOpenFolding] = useState<boolean>(true);
  const [openIssuePopup, setOpenIssuePopup] = useState<boolean>(false);
  const [issuePopupCondition, setIssuePopupCondition] = useState<any>();

  useImperativeHandle(ref, () => ({
    /**
     * @param params {
     *     elmPrjId: 프로젝트ID (필수)
     *     , excpId: 현력사ID
     * }
     */
    search: (params) => {
      console.log('params', params);
      const p = {
        ...params,
        searchSetupDailyYn: 'Y',
        searchSetupDailyDate: params?.crtnDt,
      };
      setIssueCondition(p);
      handleSearch(p);
    },
  }));

  useEffect(() => {
    props?.onChangeFolding?.(isOpenFolding);
  }, [isOpenFolding]);

  const handleSearch = useCallback(
    async (params) => {
      if (params?.elmPrjId) {
        getIssueSetupList(params)
          .then((result: IssueSetup[]) => {
            if (result !== null) {
              setIssueRowData(result);
            } else {
              openMessageBar({
                type: 'error',
                content: t(
                  'setup.msg.이슈 조회 중 오류가 발생했습니다.',
                  '이슈 조회 중 오류가 발생했습니다.'
                ),
              });
            }
          })
          .finally(() => openLoading(false));
      } else {
        setIssueRowData([]);
      }
    },
    [issueCondition?.elmPrjId]
  );

  const btnRegist = () => {
    setIssuePopupCondition({
      mode: PageMode.WRITE,
      elmPrjId: issueCondition?.elmPrjId,
      excpId: issueCondition?.excpId,
    });
    setOpenIssuePopup(true);
    /*
    navigate('/setup/issue-management/new', {
      state: {
        upprMnuUrl: location.pathname,
        mnuId: `${location.pathname}/${ManagementMode.CREATE}`,
        mnuNm: String(t('setup.label.이슈 등록', '이슈 등록')),
        mode: PageMode.WRITE,
        elmPrjId: issueCondition?.elmPrjId,
        excpId: issueCondition?.excpId,
      },
    });
    */
  };

  const btnModify = (item: any) => {
    if (item?.isuId) {
      setIssuePopupCondition({
        mode: PageMode.READ,
        isuId: item?.isuId,
      });
      setOpenIssuePopup(true);
    }
    /*
    navigate(`/setup/issue-management?isuId=${item.isuId}`, {
      state: {
        upprMnuUrl: location.pathname,
        mnuId: `${location.pathname}/${ManagementMode.MODIFY}`,
        mnuNm: String(t('setup.label.이슈 상세', '이슈 상세')),
        mode: PageMode.READ,
        isuId: item?.isuId,
      },
    });
    */
  };

  const issueLayoutDefinition = useMemo(() => {
    return [
      {
        binding: 'eqpId',
        header: String(t('setup.label.M-CODE', 'M-CODE')),
        width: 140,
        isReadOnly: true,
        align: 'center',
      },
      {
        binding: 'eqpNm',
        header: String(t('setup.label.설비명', '설비명')),
        width: 150,
        isReadOnly: true,
        align: 'left',
      },
      {
        binding: 'prjSpNm',
        header: String(t('com.label.셋업단계', '셋업단계')),
        width: 150,
        isReadOnly: true,
        align: 'center',
      },
      {
        binding: 'isuTpNm',
        header: String(t('setup.label.이슈 유형', '이슈 유형')),
        width: 120,
        isReadOnly: true,
        align: 'center',
      },
      {
        binding: 'isuTitNm',
        header: String(t('setup.label.이슈제목', '이슈제목')),
        minWidth: 160,
        width: '*',
        isReadOnly: true,
        align: 'left',
        cellTemplate: CellMaker.makeLink({
          text: '<span>${item.isuTitNm}</span>',
          click: (e, ctx) => {
            btnModify(ctx.item);
          },
        }),
      },
      {
        binding: 'isuStatNM',
        header: String(t('setup.label.상태', '상태')),
        width: 80,
        isReadOnly: true,
        align: 'center',
        cssClass: 'WijmoTag',
        cellTemplate: (item) => {
          if (item.item.isuStatCd === 'PRJ_CPLT') {
            return `<span class="yellow">${item.value}</span>`;
          } else if (item.item.isuStatCd === 'REG_CPLT') {
            return `<span class="green">${item.value}</span>`;
          } else if (item.item.isuStatCd === 'SAVE') {
            return `<span class="blue">${item.value}</span>`;
          } else {
            return `<span class="grey">${item.value}</span>`;
          }
        },
      },
      {
        binding: 'dataInsDtm',
        header: String(t('com.label.등록일자', '등록일자')),
        width: 120,
        isReadOnly: true,
        align: 'center',
      },
      {
        binding: 'dataUpdDtm',
        header: String(t('setup.label.수정일자', '수정일자')),
        width: 120,
        isReadOnly: true,
        align: 'center',
      },
    ];
  }, []);

  const onItemFormatterIssue = useEvent((panel, row, col, cell) => {
    if (CellType.Cell === panel.cellType) {
      const binding = panel.columns[col].binding;
      const item = panel.rows[row].dataItem;

      if (CellType.Cell === panel.cellType) {
        const binding = panel.columns[col].binding;
        const item = panel.rows[row].dataItem;
      }
    }
  });

  const onInitializedIssue = useEvent((grid) => {
    issueGridRef.current = grid;
    grid.itemFormatter = onItemFormatterIssue;

    grid.hostElement.addEventListener('click', (e) => {
      const len = grid.rows.length;
      if (len == 0) return;
      const ht = grid.hitTest(e);

      if (ht.row < 0 || ht.col < 0) return;
      if (ht.panel.cellType != 1) return; // 선택된 영역이 셀이 아니면 나가리.
      const item = grid.rows[ht.row].dataItem; // 선택한 row의 정보
      const col = grid.columns[ht.col];
    });
  });

  return (
    <>
      <style>
        {`
        button {
          &.btnFold {
            ${ButtonDesign.IconMD}
            width: 28px !important;
            min-width: 28px !important;
            &:active,
            &.active {
              transform: rotate(180deg); 
            }
            &.active {
              transform: rotate(180deg);
            }
          }
        }
        `}
      </style>
      <SubTitleLayout style={{ marginTop: `${isOpenFolding ? 12 : 0}px` }}>
        <SubTitleGroup>
          <h3>{t('setup.label.Issue List', 'Issue List')}</h3>
          <span className="total">
            {t('com.label.총', '총')}
            <span>{issueRowData.length ?? 0}</span>
            {t('com.label.건', '건')}
          </span>
        </SubTitleGroup>
        <ControlBtnGroup>
          {!props?.readOnly && (
            <Button css={IconButton.button} className={'save'} disableRipple onClick={btnRegist}>
              {t('com.button.이슈 등록', '이슈 등록')}
            </Button>
          )}
          <Button
            css={IconButton.button}
            className={`chevron btnFold ${isOpenFolding ? 'active' : ''}`}
            onClick={() => setOpenFolding((prev) => !prev)}
          />
        </ControlBtnGroup>
      </SubTitleLayout>
      <CustomGrid
        layoutDefinition={issueLayoutDefinition}
        rowData={issueRowData}
        initialized={onInitializedIssue}
        isSelector={false}
        autoCheck={false}
        allowPinning={false}
        isReadOnly={props?.readOnly}
        height={109}
        itemFormatter={onItemFormatterIssue}
      />

      {openIssuePopup && (
        <ProjectIssueCreatePage
          open={openIssuePopup}
          close={() => {
            setOpenIssuePopup(false);
            handleSearch(issueCondition);
          }}
          mode={issuePopupCondition?.mode || PageMode.READ}
          isuId={issuePopupCondition?.isuId || ''}
          elmPrjId={issuePopupCondition?.elmPrjId || ''}
          excpId={issuePopupCondition?.excpId || ''}
        />
      )}
    </>
  );
};

export default React.forwardRef(SetupDailyReportIssueContent);
